import { useRef, useState, type FormEvent } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useUnsavedGuard } from '@/components/useUnsavedGuard';
import { isValidAppPrefix, isValidRootDomain, normalizeRootDomain } from './domainState';
import { FIELD_WIDTH } from '@/theme';

const FIELDS = ['domain', 'appPrefix'] as const;

export interface DomainFormProps {
  defaultPrefix?: string;
  disabled?: boolean;
  saving?: boolean;
  onSave: (domain: string, appPrefix: string) => Promise<void>;
}

export function DomainForm({
  defaultPrefix = 'app',
  disabled,
  saving,
  onSave,
}: DomainFormProps) {
  const { t } = useTranslation();
  const domainRef = useRef<HTMLInputElement>(null);
  const prefixRef = useRef<HTMLInputElement>(null);
  const [domain, setDomain] = useState('');
  const [appPrefix, setAppPrefix] = useState(defaultPrefix);
  // A blur check runs only once the field has been edited (FRM-005); never on a bare tab-through.
  const [edited, setEdited] = useState<ReadonlySet<(typeof FIELDS)[number]>>(new Set());
  const fieldErrors = useFieldErrors(FIELDS, { domain: domainRef, appPrefix: prefixRef }, saving === true);
  // Edited: either field differs from the saved (initial) value (FRM-008).
  const dirty = domain !== '' || appPrefix !== defaultPrefix;
  const guard = useUnsavedGuard({ when: dirty && !disabled, name: t('unsaved.names.domain') });

  const normalized = normalizeRootDomain(domain);

  function check(field: (typeof FIELDS)[number]): string | undefined {
    if (field === 'domain') {
      return isValidRootDomain(domain) ? undefined : t('domain.field.invalidDomain');
    }
    return isValidAppPrefix(appPrefix) ? undefined : t('domain.field.appPrefixInvalid');
  }

  function blur(field: (typeof FIELDS)[number]) {
    if (!edited.has(field)) return;
    const message = check(field);
    if (message !== undefined) fieldErrors.show(field, message);
  }

  function change(field: (typeof FIELDS)[number], set: (value: string) => void, value: string) {
    set(value);
    setEdited((prev) => new Set(prev).add(field));
    fieldErrors.clear(field);
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const found: Partial<Record<(typeof FIELDS)[number], string>> = {};
    for (const field of FIELDS) {
      const message = check(field);
      if (message !== undefined) found[field] = message;
    }
    if (Object.keys(found).length > 0) {
      fieldErrors.fail(found);
      return;
    }
    fieldErrors.reset();
    try {
      await onSave(normalized, appPrefix.trim().toLowerCase());
    } catch {
      /* mutationError is already set by the hook / screen */
    }
  };

  return (
    <Stack
      component="form"
      noValidate
      spacing={2}
      onSubmit={(e) => void handleSubmit(e)}
      sx={{ maxWidth: FIELD_WIDTH.lg }}
    >
      <TextField
        label={t('domain.field.domain')}
        value={domain}
        onChange={(e) => change('domain', setDomain, e.target.value)}
        onBlur={() => blur('domain')}
        inputRef={domainRef}
        placeholder={t('domain.field.domainPlaceholder')}
        disabled={disabled}
        required
        error={fieldErrors.errors.domain !== undefined}
        helperText={fieldErrors.errors.domain ?? t('domain.field.domainHelp')}
        slotProps={{ htmlInput: { spellCheck: false } }}
      />
      <Box>
        <TextField
          label={t('domain.field.appPrefix')}
          value={appPrefix}
          onChange={(e) => change('appPrefix', setAppPrefix, e.target.value)}
          onBlur={() => blur('appPrefix')}
          inputRef={prefixRef}
          disabled={disabled}
          required
          error={fieldErrors.errors.appPrefix !== undefined}
          helperText={fieldErrors.errors.appPrefix ?? t('domain.field.appPrefixHelp')}
          slotProps={{ htmlInput: { spellCheck: false } }}
          sx={{ width: '100%' }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {appPrefix.trim() || 'app'}.{normalized || t('domain.field.domainPlaceholder')}
        </Typography>
      </Box>
      {disabled ? null : (
        <Box>
          <SubmitButton type="submit" loading={saving === true}>
            {t('domain.lookUp')}
          </SubmitButton>
        </Box>
      )}
      {guard}
    </Stack>
  );
}
